'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { cn } from '@/app/utils/cn';

const NAME_COMPONENT = 'Table of Contents';
const PARSE_FROM = 'article';
const HEADING_FOR_PARSE = 'h2, h3';
const HEADER_OFFSET = 60; // Height buffer for sticky header + some padding

const collectHeadings = () => {
    if (typeof document === 'undefined') return [];

    const mainElement = document.body.querySelector(PARSE_FROM);

    if (mainElement) {
        const headingElements = mainElement.querySelectorAll(HEADING_FOR_PARSE);

        return [...headingElements].map((heading, index) => {
            let id = heading.getAttribute('id');
            if (!id) {
                id = `heading-${index}`;
                heading.setAttribute('id', id);
            }

            return {
                id,
                text: heading.textContent || '',
                offsetTop: heading.offsetTop,
                level: parseInt(heading.tagName.replace('H', ''), 10),
                isActive: false,
                element: heading
            };
        });
    }

    return [];
};

const TableOfContents = () => {
    const [headings, setHeadings] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const handleLoad = () => {
            const newHeadings = collectHeadings();
            if (newHeadings.length > 0) {
                newHeadings[0].isActive = true;
                setHeadings(newHeadings);
            }
            setLoaded(true);
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', handleLoad);
        } else {
            handleLoad();
        }

        return () => {
            document.removeEventListener('DOMContentLoaded', handleLoad);
        };
    }, []);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const scrollPosition = window.scrollY + HEADER_OFFSET;
                    let currentHeadingId = '';

                    for (const heading of headings) {
                        if (
                            heading.offsetTop !== undefined &&
                            scrollPosition >= heading.offsetTop
                        ) {
                            currentHeadingId = heading.id;
                        }
                    }

                    setHeadings((prevHeadings) =>
                        prevHeadings.map((heading) => ({
                            ...heading,
                            isActive: heading.id === currentHeadingId
                        }))
                    );

                    ticking = false;
                });

                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [headings]);

    const handleClick = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - HEADER_OFFSET;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    if (!loaded || headings.length === 0) return null;

    return (
        <aside className="w-full rounded-lg bg-white p-8 py-6 shadow-lg">
            <h4 className="text-indigoNight dark:text-white">{NAME_COMPONENT}</h4>
            <ul
                className="overflow-y-auto py-5 pl-2"
                style={{
                    maxHeight: '24rem',
                    height: 'auto',
                    maskImage:
                        'linear-gradient(to bottom, transparent, black 2rem, black calc(100% - 2rem), transparent)',
                    WebkitMaskImage:
                        'linear-gradient(to bottom, transparent, black 2rem, black calc(100% - 2rem), transparent)'
                }}>
                {headings.map((heading, index) => (
                    <li
                        key={index}
                        className={cn(
                            `${
                                heading.isActive
                                    ? '!border-mediumOrchid text-mediumOrchid font-bold'
                                    : '!border-mediumOrchid !text-graphiteBlueGrey'
                            } relative border-l-[0.10em] pb-[0.19em] no-underline`
                        )}
                        style={{ paddingLeft: `${(heading.level - 1) * 1 + 0.625}em` }}>
                        {heading.level === 2 && (
                            <span className="bg-mediumOrchid absolute left-[-0.31em] top-[0.69em] size-[0.56em] rounded-full">
                                <Image
                                    src={'/assets/icons/blog/dot.svg'}
                                    width={16}
                                    height={16}
                                    alt="dot"
                                    unoptimized
                                />
                            </span>
                        )}
                        <a
                            href={`#${heading.id}`}
                            onClick={(e) => {
                                e.preventDefault();
                                handleClick(heading.id);
                            }}
                            style={{ textTransform: 'capitalize' }}
                            className={`py-[0.5em] hover:underline ${
                                heading.level === 1 ? 'font-semibold' : 'font-normal'
                            }`}>
                            {heading.text}
                        </a>
                    </li>
                ))}
            </ul>
        </aside>
    );
};

export default TableOfContents;
